import { generateWeekNumber } from "services/utils/dateTimeUtils";
import { SchedulingJobType } from "../SchedulingTypes";

// Generate inbox jobs from the fetched job data
export function generateInboxJobs(
    fetchedData: SchedulingJobType[],
    existingPastJobs: SchedulingJobType[],
    existingUntargetedJobs: SchedulingJobType[] = [],
    existingWeekJobs: Record<string, SchedulingJobType[]>,
    selectedDivisions: number[],
    weekStartsOnSunday = false
): {
    updatedPastJobs: SchedulingJobType[];
    updatedUntargetedJobs: SchedulingJobType[];
    updatedWeekJobs: Record<string, SchedulingJobType[]>;
    newWeeks: string[];
} {

    /* Map newly received jobs */

    // Get the date of today
    const now = new Date();

    // Initialize maps to store the newly retrieved past, untargeted and week jobs
    const newPastJobs: Record<number, SchedulingJobType> = {};
    const newWeekJobs: Record<string, Record<number, SchedulingJobType>> = {};
    const newUntargetedJobs: Record<number, SchedulingJobType> = {};

    // Initialize new job id set
    const newJobIdSet = new Set<number>();

    // Loop through the fetched jobs to add them to the maps
    for (const job of fetchedData) {

        // Add the job id to the new job id set
        newJobIdSet.add(job.id);

        // If the job doesn't have a target date, add it to the untargeted jobs map
        if (!job.target_date) {
            newUntargetedJobs[job.id] = job;
            continue;
        }

        // Get the target date of the job
        const targetDate = new Date(job.target_date);

        // Add job to the past jobs list, if the target date is before today
        if (targetDate < now) {
            newPastJobs[job.id] = job;

        // Add job to the week lists, if the target date is in the future
        } else {
            // Generate the week key
            const week = generateWeekNumber(targetDate, weekStartsOnSunday);
            const year = targetDate.getFullYear();
            const weekKey = `${year}-${week}`;

            // Add the week key to the week jobs list if it does not exist yet
            if (!newWeekJobs[weekKey]) {
                newWeekJobs[weekKey] = {};
            }

            // Put the jobs in the week of the week key
            newWeekJobs[weekKey][job.id] = job;
        }
    }


    /* Merge new and existing past jobs */

    // Initialize merged past jobs map
    const mergedPastJobsMap: Record<number, SchedulingJobType> = {};

    // Loop through the existing past jobs
    for (const job of existingPastJobs) {

        // If the job is not received in the new data, add the existing one to the new map
        if (!newJobIdSet.has(job.id)) {
            mergedPastJobsMap[job.id] = job;
        }
    }

    // Loop through the newly received past jobs to add or replace the existing jobs
    for (const [id, job] of Object.entries(newPastJobs)) {
        mergedPastJobsMap[Number(id)] = job;
    }

    // Set the updated past jobs
    let updatedPastJobs = Object.values(mergedPastJobsMap);


    /* Merge new and existing week jobs */

    // Initialize merged week jobs map
    const updatedWeekJobs: Record<string, SchedulingJobType[]> = {};

    // Get a set of all week keys in the existing- and new week jobs
    const allWeekKeys = new Set([...Object.keys(existingWeekJobs), ...Object.keys(newWeekJobs)]);

    // Loop through the weeks
    for (const weekKey of allWeekKeys) {

        // Get the existing jobs of the week
        const existing = existingWeekJobs[weekKey] ?? [];

        // Initialze merged map for the current week
        const mergedMap: Record<number, SchedulingJobType> = {};

        // Loop through the existing week jobs
        for (const job of existing) {

            // If the job is not received in the new data, add the existing one to the new map
            if (!newJobIdSet.has(job.id)) {
                mergedMap[job.id] = job;
            }
        }

        // Loop through the newly received week jobs to add or replace the existing jobs
        if (newWeekJobs[weekKey]) {
            for (const [id, job] of Object.entries(newWeekJobs[weekKey])) {
                mergedMap[Number(id)] = job;
            }
        }

        // Set the updated week jobs
        updatedWeekJobs[weekKey] = Object.values(mergedMap);
    }


    /* Merge new and existing untargeted jobs */

    // Initialize merged week jobs map
    const mergedUnscheduledMap: Record<number, SchedulingJobType> = {};

    // Loop through the existing untargeted jobs
    for (const job of existingUntargetedJobs) {

        // If the job is not received in the new data, add the existing one to the new map
        if (!newJobIdSet.has(job.id)) {
            mergedUnscheduledMap[job.id] = job;
        }
    }

    // Loop through the newly received untargeted jobs to add or replace the existing jobs
    for (const [id, job] of Object.entries(newUntargetedJobs)) {
        mergedUnscheduledMap[Number(id)] = job;
    }

    // Set the updated past jobs
    let updatedUntargetedJobs = Object.values(mergedUnscheduledMap);

    // Set the new weeks
    const newWeeks = Object.keys(newWeekJobs);


    /* Filter the jobs on the current selected division(s) if set */

    // Helper function to filter by division
    const filterByDivision = (job: SchedulingJobType) => {
        if (!selectedDivisions || selectedDivisions.length === 0) return true;
        return selectedDivisions.includes(job.division_id ?? -1);
    };

    // Filter the past-, untargeted- and week jobs
    updatedPastJobs = updatedPastJobs.filter(filterByDivision);
    updatedUntargetedJobs = updatedUntargetedJobs.filter(filterByDivision);
    for (const weekKey of Object.keys(updatedWeekJobs)) {
        updatedWeekJobs[weekKey] = updatedWeekJobs[weekKey].filter(filterByDivision);
    }

    return {
        updatedPastJobs,
        updatedUntargetedJobs,
        updatedWeekJobs,
        newWeeks,
    };
};